import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@clarix/ui';

/**
 * /password-reset-required — landing page after a force-password-reset event.
 *
 * Reached in two ways:
 *   1. At login — the login result carries `password_reset_required: true`
 *      (no token issued); routeResult() navigates here directly.
 *   2. Mid-session — the api-client interceptor detects a 401 with envelope
 *      code `PASSWORD_RESET_REQUIRED` (session revoked by the admin), clears
 *      the session, and does window.location.replace('/password-reset-required').
 *
 * This page is guard-less (no RequireAuth / RequireUnauthed) so it is reachable
 * whether the user has a session or not. "Continue to Reset Password" navigates
 * to the existing forgot-password OTP flow, which clears the flag server-side
 * once the user sets a new password.
 */
export function PasswordResetRequiredPage() {
  const navigate = useNavigate();

  // Mark the reset as pending so RequireAuth keeps this user on the reset flow
  // (manually navigating to an app route bounces back here instead of /login).
  // Cleared once they finish resetting or complete a fresh login.
  useEffect(() => {
    sessionStorage.setItem('clarix.pwreset.required', '1');
  }, []);

  return (
    <div className="mx-auto w-full max-w-sm text-center">
      <ShieldAlert
        className="mx-auto h-10 w-10 text-amber-500"
        strokeWidth={1.5}
        aria-hidden
      />
      <h1 className="mt-4 text-4xl font-semibold text-slate-900 dark:text-slate-100">
        Password Reset Required
      </h1>
      <p className="mt-3 text-xl text-slate-500">
        Your organization administrator has required all members to reset their
        password. No modules are accessible until you set a new password.
      </p>
      <p className="mt-2 text-xl text-slate-500">
        Use the password reset flow to create a new password and regain access.
        Your current password will no longer work after the reset.
      </p>
      <Button
        className="mt-6 w-full"
        onClick={() => navigate('/forgot-password')}
      >
        Continue to Reset Password
      </Button>
    </div>
  );
}
